import React, { useState, useRef } from "react";
import DriverBulkUploadService from "../services/DriverBulkUploadService";
import { toast } from "react-toastify";
import {
  FaUpload,
  FaDownload,
  FaFileUpload,
  FaTimes,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaEye,
  FaUsers,
} from "react-icons/fa";

const DriverBulkUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Processing, 4: Results
  const [selectedFile, setSelectedFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleClose = () => {
    console.log("handleClose called, isUploading:", isUploading);
    if (!isUploading) {
      console.log("Closing modal and resetting");
      resetModal();
      onClose();
    } else {
      console.log("Cannot close - upload in progress");
    }
  };

  const resetModal = () => {
    setStep(1);
    setSelectedFile(null);
    setCsvPreview(null);
    setUploadProgress(0);
    setUploadResults(null);
    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validation = DriverBulkUploadService.validateFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    setSelectedFile(file);

    try {
      const preview = await DriverBulkUploadService.getFilePreview(file, 3);
      setCsvPreview(preview);
      setStep(2);
    } catch (error) {
      toast.error(`Failed to preview file. ${error.message || "Please check the format."}`);
      setSelectedFile(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await DriverBulkUploadService.downloadCsvTemplate();
    } catch (error) {
      // Error already handled in service
    }
  };

  const handleCreateSampleTemplate = () => {
    DriverBulkUploadService.createSampleTemplate();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    console.log("🚀 Starting upload process...");

    // Test toast to ensure toast system is working
    toast.info("Starting bulk upload...");

    setIsUploading(true);
    setStep(3);
    setUploadProgress(0);

    try {
      console.log("📤 Calling DriverBulkUploadService.bulkUploadDrivers...");
      const results = await DriverBulkUploadService.bulkUploadDrivers(
        selectedFile,
        setUploadProgress
      );

      console.log("✅ Upload results received:", results);
      console.log("📊 Upload results.data:", results.data);
      console.log("🔢 Setting uploadResults to:", results.data);

      // Set the results data
      setUploadResults(results.data);
      setStep(4);

      // Call onSuccess if the operation was successful OR if we have successful uploads
      if (results.success || (results.data && results.data.successful > 0)) {
        console.log("Calling onSuccess to refresh drivers list");
        onSuccess?.(); // Refresh drivers list
      } else {
        console.log("Not calling onSuccess - no successful uploads detected");
      }
    } catch (error) {
      console.error("Upload error:", error);
      setStep(2); // Go back to preview
      toast.error("Upload failed. Please try again.");

      // Set error results to show what happened
      setUploadResults({
        successful: 0,
        failed: 1,
        totalProcessed: 1,
        errors: [
          { row: 1, error: error.message || "Upload failed", phone: "Unknown" },
        ],
        message: error.message || "Upload failed",
      });
      setStep(4); // Show results even on error
    } finally {
      setIsUploading(false);
    }
  };

  const renderUploadStep = () => (
    <div className="p-6">
      <div className="text-center mb-6">
        <FaUsers className="mx-auto text-6xl text-blue-500 mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Bulk Upload Drivers
        </h3>
        <p className="text-gray-600">
          Upload multiple drivers at once using a CSV or Excel file
        </p>
      </div>

      {/* Template Download Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
          <FaDownload className="mr-2" />
          Get CSV Template
        </h4>
        <p className="text-blue-800 text-sm mb-3">
          Download the template to see the required format and sample data
        </p>
        <div className="flex gap-2">
          <button
            onClick={handleDownloadTemplate}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm flex items-center"
          >
            <FaDownload className="mr-2" />
            Download Template
          </button>
          {/* <button
            onClick={handleCreateSampleTemplate}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm flex items-center"
          >
            <FaFileUpload className="mr-2" />
            Create Sample
          </button> */}
        </div>
      </div>

      {/* File Upload Section */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
        <FaUpload className="mx-auto text-4xl text-gray-400 mb-4" />
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept=".csv,.xlsx,.xls"
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 font-medium mb-2"
        >
          Select CSV or Excel File
        </button>
        <p className="text-gray-600 text-sm">
          Maximum file size: 10MB. CSV and Excel files (.csv, .xlsx, .xls) are supported.
        </p>
      </div>

      {/* Required Fields Info */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="font-semibold text-yellow-900 mb-2 flex items-center">
          <FaExclamationTriangle className="mr-2" />
          Required Fields
        </h4>
        <div className="text-yellow-800 text-sm">
          <p className="mb-2">
            <strong>Required:</strong> Name, Mobile Number
          </p>
          <p>
            <strong>Optional:</strong> Email, Documents, Driving Preferences
          </p>
        </div>
      </div>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="p-6">
      <div className="flex items-center mb-6">
        <FaEye className="text-blue-500 text-xl mr-3" />
        <div>
          <h3 className="text-xl font-semibold text-gray-900">File Preview</h3>
          <p className="text-gray-600">
            Review your data before uploading ({csvPreview?.totalRows} total
            rows)
          </p>
        </div>
      </div>

      {/* File Info */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">{selectedFile?.name}</p>
            <p className="text-sm text-gray-600">
              Size: {(selectedFile?.size / 1024).toFixed(2)} KB | Rows:{" "}
              {csvPreview?.totalRows}
            </p>
          </div>
          <button
            onClick={() => setStep(1)}
            className="text-blue-600 hover:text-blue-700 text-sm"
          >
            Change File
          </button>
        </div>
      </div>

      {/* Preview Table */}
      {csvPreview && (
        <div className="border rounded-lg overflow-hidden mb-6">
          <div className="overflow-x-auto max-h-80">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {csvPreview.headers.map((header, index) => (
                    <th
                      key={index}
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {csvPreview.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <td
                        key={cellIndex}
                        className="px-3 py-2 text-sm text-gray-900"
                      >
                        <div className="max-w-32 truncate" title={cell}>
                          {cell || "-"}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={() => setStep(1)}
          className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          onClick={handleUpload}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
        >
          <FaUpload className="mr-2" />
          Upload Drivers
        </button>
      </div>
    </div>
  );

  const renderProcessingStep = () => (
    <div className="p-6 text-center">
      <FaSpinner className="animate-spin mx-auto text-6xl text-blue-500 mb-4" />
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        Processing Drivers...
      </h3>
      <p className="text-gray-600 mb-6">
        Please wait while we upload and process your driver data
      </p>

      <div className="bg-gray-200 rounded-full h-3 mb-2">
        <div
          className="bg-blue-600 h-3 rounded-full transition-all duration-300"
          style={{ width: `${uploadProgress}%` }}
        />
      </div>
      <p className="text-sm text-gray-600">{uploadProgress}% Complete</p>
    </div>
  );

  const renderResultsStep = () => {
    console.log("Rendering results step with uploadResults:", uploadResults);
    console.log("uploadResults?.successful:", uploadResults?.successful);
    console.log("uploadResults?.failed:", uploadResults?.failed);
    console.log(
      "uploadResults?.totalProcessed:",
      uploadResults?.totalProcessed
    );
    console.log("uploadResults?.total:", uploadResults?.total);

    return (
      <div className="p-6">
        <div className="text-center mb-6">
          {(uploadResults?.successful || 0) > 0 ? (
            <FaCheckCircle className="mx-auto text-6xl text-green-500 mb-4" />
          ) : (
            <FaExclamationTriangle className="mx-auto text-6xl text-red-500 mb-4" />
          )}
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Upload Complete
          </h3>
        </div>

        {/* Results Summary */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {uploadResults?.totalProcessed || uploadResults?.total || 0}
            </div>
            <div className="text-sm text-blue-700">Total Processed</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {uploadResults?.successful || uploadResults?.success_count || 0}
            </div>
            <div className="text-sm text-green-700">Successful</div>
          </div>
          <div className="bg-red-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-red-600">
              {uploadResults?.failed || uploadResults?.error_count || 0}
            </div>
            <div className="text-sm text-red-700">Failed</div>
          </div>
        </div>

        {/* Debug Information */}
        {(process.env.NODE_ENV === "development" ||
          !uploadResults?.successful) && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-700 mb-2">Response Data:</h4>
            <pre className="text-xs text-gray-600 overflow-x-auto bg-white p-2 rounded">
              {JSON.stringify(uploadResults, null, 2)}
            </pre>
          </div>
        )}

        {/* Error Details */}
        {uploadResults?.errors?.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-red-900 mb-3">
              Failed Records ({uploadResults.errors.length})
            </h4>
            <div className="max-h-40 overflow-y-auto space-y-2">
              {uploadResults.errors.map((error, index) => (
                <div
                  key={index}
                  className="text-sm text-red-800 bg-red-100 rounded p-2"
                >
                  <strong>Row {error.row}:</strong>{" "}
                  {error.phone || error.mobile} - {error.error || error.message}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between">
          <button
            onClick={resetModal}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Upload Another File
          </button>
          <button
            onClick={() => {
              console.log(
                "Done button clicked - calling onSuccess then closing"
              );
              onSuccess?.(); // Ensure refresh happens
              handleClose(); // Then close modal
            }}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Done
          </button>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Bulk Upload Drivers
          </h2>
          {!isUploading && (
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 text-xl"
            >
              <FaTimes />
            </button>
          )}
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {[
              { step: 1, label: "Upload" },
              { step: 2, label: "Preview" },
              { step: 3, label: "Processing" },
              { step: 4, label: "Results" },
            ].map((item, index) => (
              <div key={item.step} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= item.step
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {item.step}
                </div>
                <div className="ml-2 text-sm font-medium text-gray-900">
                  {item.label}
                </div>
                {index < 3 && (
                  <div
                    className={`w-16 h-1 mx-4 ${
                      step > item.step ? "bg-blue-600" : "bg-gray-200"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="min-h-96">
          {step === 1 && renderUploadStep()}
          {step === 2 && renderPreviewStep()}
          {step === 3 && renderProcessingStep()}
          {step === 4 && renderResultsStep()}
        </div>
      </div>
    </div>
  );
};

export default DriverBulkUploadModal;
